<template>
  <!-- 邮件账户分组管理模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleCancel"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              邮件账户分组管理
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              创建和管理邮件账户分组，方便账户分类
            </p>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="flex space-x-4 sm:space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
                'flex-1 sm:flex-initial text-center sm:text-left',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 创建分组 Tab -->
        <div v-show="activeTab === 'create'" class="space-y-6 mb-6">
          <div>
            <label class="text-sm font-medium text-gray-700 mb-3 block">创建新分组</label>
            <div class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组名称 <span class="text-red-500">*</span></label>
                  <input
                    v-model="newGroup.name"
                    type="text"
                    placeholder="输入分组名称"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                    @keyup.enter="handleCreateGroup"
                  />
                  <div v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组描述</label>
                  <input
                    v-model="newGroup.description"
                    type="text"
                    placeholder="描述该分组用途（可选）"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                  />
                </div>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组颜色</label>
                  <div class="flex items-center gap-3">
                    <input
                      v-model="newGroup.color"
                      type="color"
                      class="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                      :disabled="submitting"
                    />
                    <span class="text-sm text-gray-600">选择分组标识颜色</span>
                  </div>
                </div>
                <div>
                  <button
                    @click="handleCreateGroup"
                    :disabled="!newGroup.name.trim() || submitting"
                    class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span v-if="submitting">创建中...</span>
                    <span v-else>创建分组</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 管理分组 Tab -->
        <div v-show="activeTab === 'manage'" class="mb-6">
          <!-- 搜索框 -->
          <div class="relative mb-4">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜索分组..."
              class="w-full h-10 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
            <MagnifyingGlassIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          <!-- 分组列表 -->
          <div v-if="loading" class="text-center py-8">
            <div class="text-gray-500">加载中...</div>
          </div>
          
          <div v-else-if="filteredGroups.length === 0" class="text-center py-8">
            <div class="text-gray-500">
              {{ searchQuery ? '未找到匹配的分组' : '暂无分组' }}
            </div>
            <div class="mt-1 text-sm text-gray-400">
              {{ searchQuery ? '尝试调整搜索条件' : '点击"创建分组"标签页添加新分组' }}
            </div>
            <button
              v-if="!searchQuery"
              @click="activeTab = 'create'"
              class="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              立即创建
            </button>
          </div>
          
          <div v-else class="space-y-2 max-h-64 overflow-y-auto">
            <div
              v-for="group in filteredGroups"
              :key="group.id"
              class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <!-- 颜色标识 -->
                <div
                  class="w-4 h-4 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: group.color }"
                ></div>
                
                <!-- 分组信息 -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-gray-900 truncate">{{ group.name }}</span>
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {{ group.account_count }} 个账户
                    </span>
                  </div>
                  <div v-if="group.description" class="text-sm text-gray-500 truncate">
                    {{ group.description }}
                  </div>
                </div>
              </div>
              
              <!-- 操作按钮 -->
              <div class="flex items-center gap-2 flex-shrink-0">
                <button
                  @click="handleEditGroup(group)"
                  class="text-blue-600 hover:text-blue-700 text-sm"
                >
                  编辑
                </button>
                <button
                  @click="handleDeleteGroup(group)"
                  :disabled="group.account_count > 0"
                  class="text-red-600 hover:text-red-700 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                  :title="group.account_count > 0 ? '该分组下还有邮件账户，无法删除' : '删除分组'"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            @click="handleCancel"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑分组模态框 -->
    <div v-if="showEditModal" class="fixed inset-0 z-60 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- 背景遮罩 -->
        <div class="fixed inset-0 bg-gray-500 opacity-75" @click="handleCloseEditModal"></div>

        <!-- 编辑模态框内容 -->
        <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-medium text-gray-900">编辑分组</h3>
            <button
              @click="handleCloseEditModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon class="h-6 w-6" />
            </button>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">分组名称 <span class="text-red-500">*</span></label>
              <input
                v-model="editingGroup.name"
                type="text"
                placeholder="输入分组名称"
                class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                :disabled="submitting"
              />
              <div v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">分组描述</label>
              <input
                v-model="editingGroup.description"
                type="text"
                placeholder="描述该分组用途（可选）"
                class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                :disabled="submitting"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">分组颜色</label>
              <div class="flex items-center gap-3">
                <input
                  v-model="editingGroup.color"
                  type="color"
                  class="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                  :disabled="submitting"
                />
                <span class="text-sm text-gray-600">选择分组标识颜色</span>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button
              @click="handleCloseEditModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              :disabled="submitting"
            >
              取消
            </button>
            <button
              @click="handleUpdateGroup"
              :disabled="!editingGroup?.name?.trim() || submitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="submitting">更新中...</span>
              <span v-else>保存修改</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import emailAccountService from '@/services/emailAccountService'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  groups: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()

// 数据状态
const loading = ref(false)
const submitting = ref(false)
const groups = ref([])
const errors = ref({})
const searchQuery = ref('')

// 标签页
const activeTab = ref('create')
const tabs = [
  { id: 'create', name: '创建分组' },
  { id: 'manage', name: '管理分组' }
]

// 新分组数据
const newGroup = ref({
  name: '',
  description: '',
  color: '#3B82F6'
})

// 编辑分组
const editingGroup = ref(null)
const showEditModal = ref(false)

// 过滤分组列表
const filteredGroups = computed(() => {
  if (!searchQuery.value) {
    return groups.value
  }
  const query = searchQuery.value.toLowerCase()
  return groups.value.filter(group => 
    group.name.toLowerCase().includes(query) ||
    (group.description && group.description.toLowerCase().includes(query))
  )
})

// 监听模态框显示状态
watch(() => props.show, async (newShow) => {
  if (newShow) {
    activeTab.value = 'create'
    resetForm()
    await loadGroups()
  }
})

// 监听父组件传递的分组数据变化
watch(() => props.groups, (newGroups) => {
  if (newGroups && newGroups.length > 0) {
    groups.value = [...newGroups]
  }
}, { immediate: true })

// 重置表单
const resetForm = () => {
  newGroup.value = {
    name: '',
    description: '',
    color: '#3B82F6'
  }
  errors.value = {}
  searchQuery.value = ''
}

// 加载分组列表（优先使用props数据，避免重复请求）
const loadGroups = async () => {
  try {
    loading.value = true
    
    // 如果父组件传递了分组数据，直接使用
    if (props.groups && props.groups.length > 0) {
      groups.value = [...props.groups]
      return
    }
    
    // 否则发起API请求
    const response = await emailAccountService.getEmailAccountGroups({
      page_size: 100
    })
    if (response.success && response.data.results) {
      groups.value = response.data.results
    } else {
      groups.value = []
      console.log('分组列表为空或加载失败')
    }
  } catch (error) {
    console.error('Failed to load groups:', error)
    toast.error('加载分组列表失败')
    groups.value = []
  } finally {
    loading.value = false
  }
}

// 创建分组
const handleCreateGroup = async () => {
  if (!newGroup.value.name.trim()) {
    errors.value = { name: '分组名称不能为空' }
    return
  }

  try {
    submitting.value = true
    errors.value = {}
    
    const groupData = {
      name: newGroup.value.name.trim(),
      description: newGroup.value.description?.trim() || '',
      color: newGroup.value.color || '#3B82F6'
    }
    
    const response = await emailAccountService.createEmailAccountGroup(groupData)
    toast.success('分组创建成功')
    
    // 立即将新创建的分组添加到本地列表
    if (response.success && response.data) {
      groups.value.push(response.data)
    } else {
      // 如果API没有返回完整数据，构造一个临时对象
      const newGroupItem = {
        id: Date.now(), // 临时ID，实际应该从API获取
        name: groupData.name,
        description: groupData.description,
        color: groupData.color,
        account_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      groups.value.push(newGroupItem)
    }
    
    resetForm()
    activeTab.value = 'manage'
    emit('success')
  } catch (error) {
    console.error('Failed to create group:', error)
    if (error.response?.data) {
      errors.value = error.response.data
    } else {
      toast.error('创建分组失败')
    }
  } finally {
    submitting.value = false
  }
}

// 编辑分组
const handleEditGroup = (group) => {
  editingGroup.value = { ...group }
  showEditModal.value = true
}

// 关闭编辑模态框
const handleCloseEditModal = () => {
  editingGroup.value = null
  showEditModal.value = false
  errors.value = {}
}

// 更新分组
const handleUpdateGroup = async () => {
  if (!editingGroup.value.name.trim()) {
    errors.value = { name: '分组名称不能为空' }
    return
  }

  try {
    submitting.value = true
    errors.value = {}
    
    const groupData = {
      name: editingGroup.value.name.trim(),
      description: editingGroup.value.description?.trim() || '',
      color: editingGroup.value.color || '#3B82F6'
    }
    
    const response = await emailAccountService.updateEmailAccountGroup(editingGroup.value.id, groupData)
    toast.success('分组更新成功')
    
    // 立即更新本地列表中的对应分组
    const index = groups.value.findIndex(g => g.id === editingGroup.value.id)
    if (index !== -1) {
      // 更新现有分组的属性
      groups.value[index] = {
        ...groups.value[index],
        name: groupData.name,
        description: groupData.description,
        color: groupData.color,
        updated_at: new Date().toISOString()
      }
    }
    
    handleCloseEditModal()
    emit('success')
  } catch (error) {
    console.error('Failed to update group:', error)
    if (error.response?.data) {
      errors.value = error.response.data
    } else {
      toast.error('更新分组失败')
    }
  } finally {
    submitting.value = false
  }
}

// 删除分组
const handleDeleteGroup = async (group) => {
  if (group.account_count > 0) {
    toast.error('该分组下还有邮件账户，无法删除')
    return
  }

  if (!confirm(`确定要删除分组"${group.name}"吗？`)) {
    return
  }

  try {
    await emailAccountService.deleteEmailAccountGroup(group.id)
    toast.success('分组删除成功')
    
    // 立即从本地列表中移除已删除的分组
    groups.value = groups.value.filter(g => g.id !== group.id)
    
    emit('success')
  } catch (error) {
    console.error('Failed to delete group:', error)
    toast.error('删除分组失败')
  }
}

// 取消
const handleCancel = () => {
  emit('close')
}

// 组件挂载时不需要加载数据（分组通过props传递，避免重复请求）
// 数据加载在 watch(() => props.show) 和 watch(() => props.groups) 中处理
</script>
